/**
 * AURA v2 AI Panel — right-side slide-over (⌘J).
 *
 * BUG-05 fix (May 1, 2026): Wired handleSend to POST /ai/chat.
 *   - Deducts 10 credits per message
 *   - Streams response into panel
 *   - Shows loading state while waiting
 *   - Handles 402 (insufficient credits) gracefully
 *   - Width 360px when open, 0 when closed (CSS transition).
 *   - Suggestions + subtitle resolved via `aiSuggestions.ts` registry
 *     (role × screen cascading lookup).
 *
 * BUG-17 fix (May 1, 2026): Clear input + preset chip on panel close.
 */
import { useEffect, useRef, useState } from 'react';
import { X, Sparkles, Send, Loader2 } from 'lucide-react';
import { api } from '@/utils/api';
import { useCreditBurn } from './useCreditBurn';
import {
  resolveSuggestions,
  resolveScreenLabel,
  resolveRoleShortLabel,
} from './aiSuggestions';
import type { DashboardRoleKey } from '../../pages/dashboard/roleLayouts';
import { useTenantConfig } from '../../hooks/useTenantConfig';
import { KiaanLockedCard } from '../shared/KiaanLockedCard';

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  creditsUsed?: number;
  error?: boolean;
}

export interface AIPanelProps {
  open: boolean;
  onClose: () => void;
  role: DashboardRoleKey | null | undefined;
  pathname: string;
  /** 2026-10-16 — rendered inside the Kiaan widget: fills its column, no own header. */
  embedded?: boolean;
}

/**
 * Apr 27, 2026 — preset banner.
 * AppShell stashes a preset object in `sessionStorage['aura-ai-preset']`
 * when a dashboard widget dispatches `aura-ai-open` with detail.preset.
 * On panel open we pull + clear that preset and surface a "context chip"
 * at the top of the panel so the user can see what they triggered. The
 * actual API call to seed a real prompt lands in Phase 4 alongside
 * /ai/chat — this is just the UX scaffolding.
 */
const PRESET_KEY = 'aura-ai-preset';

interface AiPreset {
  preset?: string;
  proposalId?: string;
  // Other optional context the dashboards forward:
  [key: string]: unknown;
}

const PRESET_LABELS: Record<string, string> = {
  weekly_summary_full:        'Read full weekly summary',
  weekly_summary_regenerate:  'Regenerate weekly summary',
  weekly_summary_generate:    'Generate weekly summary',
  production_summary_full:    'Read full production summary',
  production_summary_regenerate: 'Regenerate production summary',
  production_summary_generate:'Generate production summary',
  sales_loss_analysis:        'Analyze sales-loss reasons',
  nudge_proposal:             'Nudge proposal follow-up',
  code_map_screen:            'Explain this screen (Code map)',
};

/**
 * Payload shape returned by AURA's AI endpoints when the tenant hasn't
 * subscribed to Kiaan (HTTP 402 with error: "kiaan_subscription_required").
 */
export interface KiaanLockedPayload {
  error: 'kiaan_subscription_required';
  message: string;
  ctaUrl: string;
  ctaLabel: string;
}

export function AIPanel({ open, onClose, role, pathname, embedded = false }: AIPanelProps) {
  const [input, setInput] = useState('');
  const [activePreset, setActivePreset] = useState<AiPreset | null>(null);
  const [kiaanLocked, setKiaanLocked] = useState<KiaanLockedPayload | null>(null);
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [isSending, setIsSending] = useState(false);
  const chatEndRef = useRef<HTMLDivElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const { fire, render: renderBursts } = useCreditBurn();
  const { aiAssistantName } = useTenantConfig();

  // Resolve on every render — cheap, deterministic, pure-function output.
  const suggestions = resolveSuggestions(role, pathname);
  const screenLabel = resolveScreenLabel(pathname);
  const roleLabel = resolveRoleShortLabel(role);
  const subtitle = role ? `${roleLabel} · ${screenLabel}` : screenLabel;

  // Listen for 402 "kiaan_subscription_required" dispatched by the API interceptor.
  useEffect(() => {
    const handler = (e: CustomEvent<KiaanLockedPayload>) => {
      setKiaanLocked(e.detail);
    };
    window.addEventListener('aura:kiaan:locked', handler as EventListener);
    return () => window.removeEventListener('aura:kiaan:locked', handler as EventListener);
  }, []);

  // Close on ESC when open
  useEffect(() => {
    if (!open) return;
    const h = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.preventDefault(); onClose(); }
    };
    document.addEventListener('keydown', h);
    return () => document.removeEventListener('keydown', h);
  }, [open, onClose]);

  // Apr 27, 2026 — preset consumption. On panel open, drain the preset key
  // and seed both the preset chip and the input field with a sensible draft.
  useEffect(() => {
    if (!open) return;
    if (typeof window === 'undefined') return;
    let raw: string | null = null;
    try { raw = window.sessionStorage.getItem(PRESET_KEY); } catch {}
    if (!raw) return;
    try {
      const parsed = JSON.parse(raw) as AiPreset;
      setActivePreset(parsed);
      // Seed the input with a human-readable phrasing of the preset so the
      // user can edit before sending. Only seed if input is currently empty.
      if (parsed.preset && PRESET_LABELS[parsed.preset]) {
        setInput((prev) => prev.trim() ? prev : PRESET_LABELS[parsed.preset!]);
      }
      window.sessionStorage.removeItem(PRESET_KEY);
    } catch {
      // Ignore malformed preset payloads.
      try { window.sessionStorage.removeItem(PRESET_KEY); } catch {}
    }
  }, [open]);

  // BUG-17 fix: Clear preset chip AND input text when panel closes, so
  // re-opening without a new preset shows a clean state.
  useEffect(() => {
    if (!open) {
      setActivePreset(null);
      setInput('');
    }
  }, [open]);

  // Auto-scroll chat to bottom when new messages arrive
  useEffect(() => {
    if (chatMessages.length > 0) {
      chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [chatMessages]);

  const handleSuggest = (e: React.MouseEvent<HTMLButtonElement>, cost: number) => {
    fire(e, cost);
  };

  // BUG-05 fix: POST /ai/chat, deduct credits, display response in panel.
  const handleSend = async (e: React.MouseEvent<HTMLButtonElement>) => {
    const trimmed = input.trim();
    if (!trimmed || isSending) return;

    // Optimistically add user message to chat
    const userMsg: ChatMessage = { role: 'user', content: trimmed };
    setChatMessages((prev) => [...prev, userMsg]);
    setInput('');
    setIsSending(true);

    try {
      const res = await api.post('/ai/chat', {
        message: trimmed,
        context: { pathname },
      });
      const data = res.data?.data;
      const reply = data?.reply ?? 'No response received.';
      const creditsUsed = data?.creditsUsed ?? 10;
      fire(e, creditsUsed);
      setChatMessages((prev) => [
        ...prev,
        { role: 'assistant', content: reply, creditsUsed },
      ]);
    } catch (err: any) {
      const errorMsg =
        err?.response?.data?.error === 'Insufficient AI credits'
          ? 'You have insufficient AI credits. Please top up to continue.'
          : err?.response?.data?.error ?? 'Something went wrong. Please try again.';
      setChatMessages((prev) => [
        ...prev,
        { role: 'assistant', content: errorMsg, error: true },
      ]);
    } finally {
      setIsSending(false);
    }
  };

  // Allow Enter (without Shift) to submit the message
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (input.trim() && !isSending) {
        // Create a synthetic mouse event pointing to the send button's position
        const syntheticEvent = { currentTarget: e.currentTarget } as unknown as React.MouseEvent<HTMLButtonElement>;
        handleSend(syntheticEvent);
      }
    }
  };

  return (
    <div
      ref={panelRef}
      className="relative flex flex-col overflow-hidden"
      style={{
        width: embedded ? '100%' : open ? 360 : 0,
        transition: 'width .24s cubic-bezier(.2,.8,.3,1)',
        background: embedded ? 'transparent' : 'var(--ivory-1)',
        borderLeft: open && !embedded ? '1px solid var(--line)' : 'none',
        flexShrink: 0,
      }}
    >
      <div style={{ width: embedded ? '100%' : 360, display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
        {/* Header */}
        {!embedded && (
        <div
          className="flex items-center gap-2 border-b px-4 py-3"
          style={{ borderColor: 'var(--line)' }}
        >
          <div
            className="flex items-center justify-center rounded-full"
            style={{
              width: 24, height: 24,
              background: 'linear-gradient(135deg, #C98A3B, #B87A2E)',
            }}
          >
            <Sparkles size={13} style={{ color: '#fff' }} />
          </div>
          <div className="flex-1">
            <div className="text-[13px] font-semibold" style={{ color: 'var(--ink-0)' }}>
              {aiAssistantName}, your AI assistant
            </div>
            <div className="text-[11px]" style={{ color: 'var(--ink-3)' }}>
              {subtitle}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex items-center justify-center rounded-md"
            style={{
              width: 26, height: 26,
              color: 'var(--ink-3)',
              background: 'transparent',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--ivory-2)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
            title="Close"
          >
            <X size={14} />
          </button>
        </div>
        )}

        {/* Apr 27, 2026 — preset context chip (rendered when a dashboard
            widget triggered the AI panel via aura-ai-open with a preset). */}
        {activePreset?.preset && (
          <div
            className="border-b px-4 py-2"
            style={{
              borderColor: 'var(--line)',
              background: 'var(--saffron-50, #FBF3DF)',
              fontSize: 11,
              color: 'var(--ink-1)',
            }}
          >
            <span style={{ color: 'var(--saffron-600, #B87A2E)', fontWeight: 600 }}>
              ✦ Context:
            </span>{' '}
            <span>
              {PRESET_LABELS[activePreset.preset] ?? activePreset.preset}
            </span>
            {activePreset.proposalId && (
              <span style={{ marginLeft: 6, color: 'var(--ink-3)', fontFamily: 'var(--ff-mono)' }}>
                #{String(activePreset.proposalId).slice(-6)}
              </span>
            )}
          </div>
        )}

        {/* Body */}
        <div className="relative flex-1 overflow-y-auto px-3.5 py-3">
          {/* 402 Kiaan locked card — shown instead of normal content when backend returns kiaan_subscription_required */}
          {kiaanLocked && (
            <KiaanLockedCard
              message={kiaanLocked.message}
              ctaUrl={kiaanLocked.ctaUrl}
              ctaLabel={kiaanLocked.ctaLabel}
              onDismiss={() => setKiaanLocked(null)}
            />
          )}
          {renderBursts()}

          {/* BUG-05: Chat history — shown when there are messages; hides suggestions/recents */}
          {chatMessages.length > 0 ? (
            <div className="flex flex-col gap-2.5 pb-2">
              {chatMessages.map((msg, i) => (
                <div
                  key={i}
                  className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className="max-w-[88%] rounded-xl px-3 py-2 text-[12.5px] leading-relaxed"
                    style={
                      msg.role === 'user'
                        ? { background: 'var(--ink-0)', color: 'var(--ivory-0)' }
                        : {
                            background: msg.error ? 'var(--error-bg, #FEE2E2)' : 'var(--ivory-2)',
                            color: msg.error ? 'var(--error-ink, #DC2626)' : 'var(--ink-0)',
                            border: '1px solid var(--line)',
                          }
                    }
                  >
                    <div style={{ whiteSpace: 'pre-wrap' }}>{msg.content}</div>
                    {msg.role === 'assistant' && msg.creditsUsed !== undefined && !msg.error && (
                      <div
                        className="mt-1 flex items-center gap-1 text-[10px]"
                        style={{ color: 'var(--ink-3)' }}
                      >
                        <Sparkles size={9} />
                        {msg.creditsUsed} credits used
                      </div>
                    )}
                  </div>
                </div>
              ))}
              {isSending && (
                <div className="flex justify-start">
                  <div
                    className="flex items-center gap-2 rounded-xl px-3 py-2 text-[12.5px]"
                    style={{ background: 'var(--ivory-2)', border: '1px solid var(--line)', color: 'var(--ink-3)' }}
                  >
                    <Loader2 size={12} className="animate-spin" />
                    Thinking…
                  </div>
                </div>
              )}
              <div ref={chatEndRef} />
            </div>
          ) : (
            <>
              <div
                className="mb-2 text-[10px] font-semibold uppercase tracking-wider"
                style={{ color: 'var(--ink-3)' }}
              >
                Try these
              </div>
              <div className="mb-4 flex flex-col gap-1.5">
                {suggestions.map((s, i) => (
                  <button
                    key={`${s.text}-${i}`}
                    type="button"
                    onClick={(e) => handleSuggest(e, s.cost)}
                    className="flex items-start justify-between gap-2 rounded-md border px-2.5 py-2 text-left text-[12.5px]"
                    style={{
                      background: 'var(--ivory-0)',
                      borderColor: 'var(--line)',
                      color: 'var(--ink-1)',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--ivory-2)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--ivory-0)'; }}
                  >
                    <span className="flex-1 whitespace-normal leading-snug">{s.text}</span>
                    <span
                      className="flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px]"
                      style={{
                        background: 'var(--saffron-50)',
                        color: 'var(--saffron-600)',
                        flexShrink: 0,
                      }}
                    >
                      <Sparkles size={11} />
                      {s.cost}
                    </span>
                  </button>
                ))}
              </div>

            </>
          )}
        </div>

        {/* Composer */}
        <div className="border-t p-2.5" style={{ borderColor: 'var(--line)' }}>
          <div className="relative">
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={isSending}
              placeholder={`Ask ${aiAssistantName} anything about this page…`}
              className="w-full resize-none rounded-lg border px-2.5 py-2 text-[13px] outline-none"
              style={{
                minHeight: 60,
                background: 'var(--ivory-0)',
                borderColor: 'var(--line)',
                color: 'var(--ink-0)',
                opacity: isSending ? 0.6 : 1,
              }}
            />
            <button
              type="button"
              onClick={handleSend}
              disabled={!input.trim() || isSending}
              className="absolute bottom-2 right-2 flex items-center gap-1 rounded-md px-2 py-1 text-[11px] font-medium"
              style={{
                background: (input.trim() && !isSending) ? 'var(--ink-0)' : 'var(--ivory-3)',
                color: (input.trim() && !isSending) ? 'var(--ivory-0)' : 'var(--ink-3)',
                cursor: (input.trim() && !isSending) ? 'pointer' : 'not-allowed',
              }}
            >
              {isSending ? <Loader2 size={11} className="animate-spin" /> : <Send size={11} />}
              {isSending ? 'Sending…' : 'Send · 10'}
            </button>
          </div>
          <div
            className="mt-1.5 flex items-center justify-between text-[11px]"
            style={{ color: 'var(--ink-3)' }}
          >
            <span>10 credits per message · Enter to send</span>
            <span className="font-mono">⌘J to toggle</span>
          </div>
        </div>
      </div>
    </div>
  );
}
